import React, { useEffect, useRef, useState } from 'react';
import './GazeTracker.scss';

interface GazeTrackerProps {
    active: boolean;
    onCalibrationComplete: () => void;
    showCursor: boolean;
}

export const GazeTracker: React.FC<GazeTrackerProps> = ({
    active,
    onCalibrationComplete,
    showCursor
}) => {
    const [isCalibrating, setIsCalibrating] = useState(false);
    const [gazePosition, setGazePosition] = useState({ x: 0, y: 0 });
    const initializedRef = useRef(false);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data === "calibration_done") {
                console.log("Calibration successful from iframe!");
                setIsCalibrating(false);
                onCalibrationComplete();

                // Resume parent webgazer
                if (window.webgazer) {
                    window.webgazer.resume();
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [onCalibrationComplete]);

    useEffect(() => {
        if (active && !initializedRef.current) {
            console.log("Initializing WebGazer...");
            if (typeof window.webgazer !== 'undefined') {
                try {
                    (window as any).saveDataAcrossSessions = true;

                    window.webgazer
                        .setGazeListener((data, elapsedTime) => {
                            if (data) {
                                setGazePosition({ x: data.x, y: data.y });
                            }
                        })
                        .begin();

                    window.webgazer.showVideo(false);
                    window.webgazer.showPredictionPoints(false);
                    initializedRef.current = true;
                    setIsCalibrating(true);

                    // Pause parent webgazer while iframe is running to avoid conflict
                    window.webgazer.pause();
                } catch (err) {
                    console.error("Error setting up WebGazer:", err);
                }
            }
        } else if (!active && initializedRef.current) {
            window.webgazer.pause();
        } else if (active && initializedRef.current && !isCalibrating) {
            window.webgazer.resume();
        }
    }, [active, isCalibrating]);

    if (!active) return null;

    return (
        <div className="gaze-tracker">
            {/* Real-time Gaze Cursor */}
            {showCursor && !isCalibrating && (
                <div
                    className="gaze-cursor"
                    style={{ left: gazePosition.x, top: gazePosition.y }}
                />
            )}

            {/* Calibration Iframe Overlay */}
            {isCalibrating && (
                <div className="calibration-overlay" style={{ pointerEvents: 'all' }}>
                    <iframe
                        src="/webgazer/calibration.html"
                        style={{ width: '100vw', height: '100vh', border: 'none' }}
                        title="WebGazer Calibration"
                    />
                </div>
            )}
        </div>
    );
};
